import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LifeBuoy, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useSeoTitle } from "@/lib/useSeoTitle";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const CATEGORIES = [
  { id: "general", label: "General feedback" },
  { id: "password", label: "Password / account recovery" },
  { id: "bug", label: "Bug report" },
  { id: "feature", label: "Feature request" },
  { id: "nsfw", label: "Mature Realms / NSFW" },
];

export default function Support() {
  const nav = useNavigate();
  const { user } = useAuth();
  useSeoTitle({
    title: "Support & Contact — TalesTalesTales Help Center",
    description: "Get help with TalesTalesTales: billing, bugs, account access, feature requests. Open a support ticket and our team will reply by email.",
    path: "/support",
  });
  const [category, setCategory] = useState("general");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [contactEmail, setContactEmail] = useState(user?.email || "");
  const [busy, setBusy] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject || !message || !contactEmail) {
      return toast.error("Subject, message and contact email are required.");
    }
    setBusy(true);
    try {
      const r = await api.post("/support/tickets", {
        category, subject, message, contact_email: contactEmail,
      });
      setSubmittedId(r.data.id);
      toast.success("Your message reached the keep. We'll reply via email.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="support-page" className="min-h-screen bg-[#0f0d11]">
      <Nav />
      <div id="main-content" className="max-w-3xl mx-auto px-6 py-12">
        <div className="rise mb-8 text-center">
          <div className="inline-flex items-center gap-2 font-ui text-xs tracking-[0.35em] text-amber-400/80 uppercase mb-3">
            <LifeBuoy className="w-3.5 h-3.5" /> Send a raven
          </div>
          <h1 className="font-display text-5xl text-amber-50">Contact us</h1>
          <p className="font-body text-amber-100/65 mt-3 max-w-xl mx-auto italic">
            Forgot your security answer? Found a bug? Want a new realm built? Tell us here — we read every scroll.
          </p>
        </div>

        {submittedId ? (
          <div data-testid="support-success" className="parchment gold-border rounded-xl p-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
            <h2 className="font-display text-2xl text-amber-50 mb-2">Ticket received</h2>
            <p className="font-body text-amber-100/70">
              Reference: <span className="font-mono text-amber-200">{submittedId.slice(0, 8)}</span>
            </p>
            <p className="font-body text-amber-100/60 italic mt-3">
              We&apos;ll reach out to <span className="text-amber-200">{contactEmail}</span>. Reply may take 1–3 days.
            </p>
            <div className="mt-6 flex gap-2 justify-center">
              <Button
                data-testid="support-new-ticket"
                variant="outline"
                onClick={() => { setSubmittedId(null); setSubject(""); setMessage(""); }}
                className="border-amber-500/50 text-amber-200 hover:bg-amber-900/20 font-ui"
              >
                Send another
              </Button>
              <Button
                onClick={() => nav(user ? "/dashboard" : "/")}
                className="bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui"
              >
                {user ? "Back to dashboard" : "Back home"}
              </Button>
            </div>
          </div>
        ) : (
          <form
            data-testid="support-form"
            onSubmit={submit}
            className="parchment gold-border rounded-xl p-7 space-y-4"
          >
            <div>
              <Label className="font-ui text-amber-200/80 text-xs">Topic</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger
                  data-testid="support-category"
                  className="mt-1 bg-[#15121a] border-amber-700/30 text-amber-100"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1d181c] border-amber-700/30">
                  {CATEGORIES.map((c) => (
                    <SelectItem
                      key={c.id} value={c.id}
                      data-testid={`support-cat-${c.id}`}
                      className="text-amber-100"
                    >
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-ui text-amber-200/80 text-xs">Reply-to email</Label>
              <Input
                data-testid="support-email"
                type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)}
                className="mt-1 bg-[#15121a] border-amber-700/30 text-amber-100"
                required
              />
            </div>
            <div>
              <Label className="font-ui text-amber-200/80 text-xs">Subject</Label>
              <Input
                data-testid="support-subject"
                value={subject} onChange={(e) => setSubject(e.target.value)}
                placeholder="A short title"
                className="mt-1 bg-[#15121a] border-amber-700/30 text-amber-100"
                maxLength={160} required
              />
            </div>
            <div>
              <Label className="font-ui text-amber-200/80 text-xs">Message</Label>
              <Textarea
                data-testid="support-message"
                value={message} onChange={(e) => setMessage(e.target.value)}
                rows={6}
                placeholder="Describe what's going on — error messages, what you tried, anything you remember..."
                className="mt-1 bg-[#15121a] border-amber-700/30 text-amber-100"
                maxLength={4000} required
              />
              <div className="text-right text-[10px] text-amber-200/50 mt-1">
                {message.length} / 4000
              </div>
            </div>
            <Button
              data-testid="support-submit"
              type="submit" disabled={busy}
              className="w-full bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui h-11"
            >
              <Send className="w-4 h-4 mr-2" />
              {busy ? "Sending..." : "Send to the keep"}
            </Button>
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
}
